import { camelize } from "humps";
import compact from "lodash/compact";
import head from "lodash/head";
import isEqual from "lodash/isEqual";
import unionBy from "lodash/unionBy";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { LegacyForm } from "PFApp/components/form/legacy_form";
import PositionPeriodPicker from "PFApp/profiles/edit/fields/position_period_picker";
import PROFILE_SECTIONS from "PFApp/profiles/profile_sections";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { InlineCalendar } from "PFComponents/calendar/inline_calendar";
import SidePanel from "PFComponents/side_panel/side_panel";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import api from "PFCore/api";
import { isProfileFieldPermitted } from "PFCore/helpers/profile_permissions";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { editProfile } from "PFCore/services/admin/profiles/edit_profile";
import { editCurrentProfile } from "PFCore/services/current_profile";
import { AccessLevel, CustomType, PermissionsGroup, Position, Profile } from "PFTypes";
import { KnownProfileCustomFields } from "PFTypes/profile";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./edit_position_form.module.scss";
import { sanitizePosition } from "./positon_custom_types";

type PositionFormData = Partial<
  Pick<Position, "id" | "main" | "current" | "duration" | "startDate" | "endDate" | "summary" | "jobTitle">
> &
  Partial<KnownProfileCustomFields>;

type EditPositionFormProps = {
  position?: PositionFormData | Position;
  permissionsGroup?: PermissionsGroup;
  profile?: Profile;
  adminPage?: boolean;
  handleSuccess?: (newRecord?: Position) => void;
  handleRemove?: (data: any) => void;
  onClose?: () => void;
};

const EditPositionForm = ({
  position: positionProp = {},
  profile,
  adminPage,
  handleSuccess,
  handleRemove,
  permissionsGroup,
  onClose
}: EditPositionFormProps) => {
  const position = positionProp as PositionFormData;
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("profiles", { keyPrefix: "common.experience" });
  const { t: tTranslation } = useTranslation();
  const { loadForSections, customTypes } = useCustomTypes();

  const profilePermissionsGroup = adminPage ? permissionsGroup : profile?.permissionsGroup;

  const { formatISODate } = useDateFormatter();
  const [errors, setErrors] = useState<Record<string, any>>({});

  const [formData, setFormData] = useState<PositionFormData>({ ...position });

  const isMain = position.main;
  const isNew = !position.id;

  const positionCustomTypes = compact(
    Object.keys(position).map((name) => customTypes.find((ct) => ct?.name === name))
  );

  let experienceCustomTypes = unionBy(
    loadForSections(
      isMain
        ? [PROFILE_SECTIONS.current_experience_title_1, PROFILE_SECTIONS.current_experience_title_2]
        : [
            PROFILE_SECTIONS.past_experience_title_1,
            PROFILE_SECTIONS.past_experience_title_2,
            PROFILE_SECTIONS.profile_past_experience
          ]
    ),
    positionCustomTypes,
    "name"
  );

  const prefix = isMain ? "mainPosition" : "otherPositions";
  const targetGroupId = profile?.permissionsGroup?.id;

  experienceCustomTypes = experienceCustomTypes
    .filter((ct) =>
      isProfileFieldPermitted(
        profilePermissionsGroup,
        targetGroupId,
        `${prefix}/${camelize(ct.name)}`,
        AccessLevel.ReadOnly
      )
    )
    .sort((customTypeA, customTypeB) => customTypeA.display_as!.localeCompare(customTypeB.display_as!));

  const whiteList: Record<string, boolean> = {
    id: true,
    main: true,
    _destroy: true
  };

  const disableDate = () =>
    !isProfileFieldPermitted(
      profilePermissionsGroup,
      profile?.permissionsGroup?.id,
      `${prefix}/dates`,
      AccessLevel.ReadWrite
    );

  const disableSummary = () =>
    !isProfileFieldPermitted(
      profilePermissionsGroup,
      profile?.permissionsGroup?.id,
      `${prefix}/summary`,
      AccessLevel.ReadWrite
    );

  const showDates = () =>
    isProfileFieldPermitted(
      profilePermissionsGroup,
      profile?.permissionsGroup?.id,
      `${prefix}/dates`,
      AccessLevel.ReadOnly
    );

  const showSummary = () =>
    isProfileFieldPermitted(
      profilePermissionsGroup,
      profile?.permissionsGroup?.id,
      `${prefix}/summary`,
      AccessLevel.ReadOnly
    );

  const showCustomType = (customType: CustomType) =>
    isProfileFieldPermitted(
      profilePermissionsGroup,
      profile?.permissionsGroup?.id,
      `${prefix}/${camelize(customType.name)}`,
      AccessLevel.ReadOnly
    );

  const disableCustomType = (customType: CustomType) =>
    !isProfileFieldPermitted(
      profilePermissionsGroup,
      profile?.permissionsGroup?.id,
      `${prefix}/${camelize(customType.name)}`,
      AccessLevel.ReadWrite
    );

  const handleSuccessFunc = () => {
    showGrowl({ message: isNew ? t("position.created") : t("position.updated") });
    handleSuccess && handleSuccess();
  };

  const handleRemoveFunc = () => {
    const titleCustomType = head(
      loadForSections(
        position.main ? PROFILE_SECTIONS.current_experience_title_1 : PROFILE_SECTIONS.past_experience_title_1
      )
    );

    if (isNew) {
      if (!isEqual(formData, position)) {
        if (window.confirm(t("confirmCloseDialog"))) {
          handleRemove && handleRemove(formData);
        }
      } else {
        handleRemove && handleRemove(formData);
      }
    } else {
      const experienceTitle = titleCustomType && position[camelize(titleCustomType.name)]?.[0]?.value;
      const message = experienceTitle
        ? tTranslation("sureToDelete", { key: experienceTitle })
        : t("position.edit.sureToDeletePosition");

      if (window.confirm(message)) {
        handleRemove &&
          handleRemove({
            positions: [
              {
                _destroy: true,
                id: position.id
              }
            ]
          });
      }
    }
  };

  const handleError = (errors: Record<string, any>) => {
    setErrors(errors);
    if (errors.base) {
      showGrowl({
        message: errors.base,
        kind: "error"
      });
    }
  };

  const handleMainPositionChange = () => {
    if (formData.main) {
      api({
        url: adminPage ? `admin/profiles/${profile!.id}` : "me",
        data: { mainPositionId: position.id },
        method: "PUT"
      }).catch(() => {
        showGrowl({
          message: t("position.edit.mainPosition.error"),
          kind: "error"
        });
      });
    }
  };

  if (!disableDate()) {
    whiteList.current = true;
    whiteList.startDate = true;
    whiteList.endDate = true;
    whiteList.duration = true;
  }

  if (!disableSummary()) {
    whiteList.summary = true;
  }

  // whitelist Custom Types
  experienceCustomTypes.forEach((ct) => {
    if (!disableCustomType(ct)) {
      whiteList[camelize(ct.name)] = true;
    }
  });

  const handleSubmit = () => {
    handleMainPositionChange();

    const positionToSave = sanitizePosition(formData, whiteList, customTypes, adminPage);

    if (adminPage) {
      return editProfile(profile!.id, positionToSave);
    }

    return editCurrentProfile(positionToSave);
  };

  const customTypeToComponent = (customType: CustomType) => {
    const { name } = customType;
    const camelizedName = camelize(name);
    const values = formData[camelizedName] || [];

    if (!showCustomType(customType)) {
      return;
    }

    return (
      <CustomValuesEditField
        letClear
        key={name}
        position={positionProp as Position}
        customType={customType}
        values={values}
        qaIdPrefix="profile-position"
        errors={errors[`positions[0][${camelizedName}]`]}
        adminPage={adminPage}
        profile={profile}
        handleChange={(values) => setFormData({ ...formData, [camelizedName]: values })}
        perPage={250}
      />
    );
  };

  return (
    <SidePanel
      show
      fullHeight
      isOnTop
      size="medium"
      title={isNew ? t("position.newExperience") : t("position.editPosition")}
      onClose={onClose}
    >
      <LegacyForm
        isNew={isNew}
        isDirty={!isEqual(formData, position)}
        onSuccess={handleSuccessFunc}
        onError={handleError}
        onRemove={isMain ? undefined : handleRemoveFunc}
        onSubmit={handleSubmit}
        qaIdPrefix="profile-edit-past-experience"
        blockNavigationOnChange
        growlErrors
      >
        <div className={css.form}>
          {experienceCustomTypes.map(customTypeToComponent)}
          {!isMain && showDates() && (
            <PositionPeriodPicker
              label={t("position.edit.startDate.label" as unknown as TemplateStringsArray)}
              error={
                errors["positions[0][duration]"] ||
                errors["positions[0][startDate]"] ||
                errors["positions[0][endDate]"] ||
                errors["positions[0][current]"]
              }
              showLabel
              letDuration
              duration={formData.duration || ""}
              currentPosition={formData.current}
              startDate={formData.startDate ? formatISODate(formData.startDate) : null}
              endDate={formData.endDate ? formatISODate(formData.endDate) : null}
              maxDate={formatISODate()}
              locked={disableDate()}
              lockedTip={t("position.edit.noPermissionsTip")}
              handleToggleClick={(current) =>
                setFormData((currentFormData) => ({ ...currentFormData, current }))
              }
              handleChange={(startDate, endDate, durationValue, showDuration) =>
                setFormData((currentFormData) => ({
                  ...currentFormData,
                  startDate: showDuration ? null : startDate,
                  endDate: showDuration ? null : endDate,
                  duration: (showDuration && durationValue) || null
                }))
              }
            />
          )}
          {isMain && showDates() && (
            <div data-qa-id="profile-edit-current-position-start-date">
              <InlineCalendar
                disabled={disableDate()}
                selectedDate={formData.startDate ? formatISODate(formData.startDate) : null}
                label={t("position.edit.startDate") as string}
                error={errors["positions[0][startDate]"]}
                handleChange={(startDate) => setFormData({ ...formData, startDate })}
                locked={disableDate()}
                lockedTip={t("position.edit.noPermissionsTip")}
              />
            </div>
          )}

          {showSummary() && (
            <InputFieldSet
              inputType="textarea"
              label={t("position.edit.summary")}
              qaId="summary"
              value={formData.summary || ""}
              locked={disableSummary()}
              lockedTip={t("position.edit.noPermissionsTip")}
              onChange={(summary) => setFormData({ ...formData, summary })}
            />
          )}
          {!isNew && (
            <Toggle
              label={t("position.edit.mainPosition.label")}
              disabled={isMain}
              checked={isMain ? isMain : formData.main}
              inline
              tip={isMain ? t("position.edit.mainPosition.tipIs") : t("position.edit.mainPosition.tipSet")}
              onChange={(main) => setFormData({ ...formData, main })}
            />
          )}
        </div>
      </LegacyForm>
    </SidePanel>
  );
};

export default EditPositionForm;
